import { h, ComponentChildren } from "preact";
import { msg } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import * as I from "../../../../utils/icons";

import { useEffect, useRef } from "preact/hooks";
import { PaymentNotification, TransactionStatusPoller } from "../../../../../../services/payment-gateway";

export function Spinner() {
	return <GatewayContainer>
		<div role="status" class="w-full flex flex-col items-center gap-4">
			<svg aria-hidden="true" class="w-32 h-32 text-white animate-spin dark:text-gray-600 fill-blue-600" viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="currentColor" />
				<path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentFill" />
			</svg>
			<span class="text-4xl font-thin"><Trans>Please Wait</Trans></span>
		</div>
	</GatewayContainer>;
}

interface ErrorMessageProps {
	error: string;
}
export function ErrorMessage({ error }: ErrorMessageProps) {
	return <GatewayContainer>
		<div role="status" class="w-full flex flex-col items-center gap-4">
			<I.CircleXmark class="h-32 text-red-600" />
			<span class="text-4xl text-red-600 text-center"><Trans>Error</Trans></span>
			<span class="text-base">{error}</span>
		</div>
	</GatewayContainer>
}

export function GatewayContainer({ children }: { children: ComponentChildren }) {
	return <div class="flex flex-col items-center w-full max-w-sm backdrop-blur-xl bg-white/50 rounded mt-4 p-4 px-6">
		{children}
	</div>;
}

export function SuccessMessage({ children }: { children: ComponentChildren }) {
	return <div role="status" class="w-full flex flex-col items-center gap-4">
		<I.CircleCheck class="h-32 text-green-500" />
		{children}
	</div>
}

export function PendingMessage({ children }: { children: ComponentChildren }) {
	return <div role="status" class="w-full flex flex-col items-center gap-4">
		<I.HourglassSlim class="h-32 text-green-500" />
		{children}
	</div>
}

export function SuccessPage({ onRepeat }: { onRepeat: (e: Event) => void }) {
	return <GatewayContainer>
		<SuccessMessage>
			<span class="text-4xl text-green-500 text-center"><Trans>Success</Trans></span>
			<span class="text-base"><Trans>Your transaction is successful</Trans></span>
			<div class="flex justify-center gap-2">
				<a class="px-5 py-3 text-center rounded bg-blue-whale-700 text-slate-50 border-blue-whale-800 text-lg flex gap-1 items-center" href="/">
					<I.HomeIcon class="h-4 fill-current" /><Trans>Home</Trans>
				</a>
				<a
					class="px-5 py-3 text-center rounded bg-amber-600 text-slate-950 border-amber-700 text-lg flex gap-1 items-center"
					href="javascript:void(0);"
					onClick={onRepeat}
				>
					<I.RefreshIcon class="h-4 fill-current" /><Trans>Repeat</Trans>
				</a>
			</div>
		</SuccessMessage>
	</GatewayContainer>;
}

export function WaitSuccessPage() {
	return <GatewayContainer>
		<SuccessMessage>
			<span class="text-4xl text-green-500 text-center"><Trans>Please wait</Trans></span>
			<span class="text-base"><Trans>Your will be notified when your amount is available</Trans></span>
		</SuccessMessage>
	</GatewayContainer>;
}

interface FullSizeIframeProps {
	formurl: string;
}
export function FullSizeIframe({ formurl }: FullSizeIframeProps) {
	return <div class="border-0 w-full h-full bg-white text-slate-600 grow flex flex-col relative">
		<iframe src={formurl} class="border-0 w-full h-full bg-white grow" />
	</div>;
}

interface TransactionPollingComponentProps {
	statusUrl: string;
	onEvent: (status: PaymentNotification) => void;
	children: ComponentChildren;
}

export function TransactionPollingPage({ statusUrl, onEvent, children }: TransactionPollingComponentProps) {
	const poller = useRef<TransactionStatusPoller | null>(null);

	useEffect(() => {
		if (!statusUrl) return;
		poller.current = new TransactionStatusPoller(statusUrl);
		poller.current.addListener(onEvent);
		poller.current.start();

		return () => {
			poller.current?.stop();
		};
	}, [statusUrl, onEvent]);

	return <GatewayContainer>
		<PendingMessage>
			{children}
		</PendingMessage>
	</GatewayContainer>;
}